const TOC_ITEMS = [
  { id: "s1", label: "Общие положения" },
  { id: "s2", label: "Общий принцип: платежи не возвращаются" },
  { id: "s3", label: "Услуги, на которые распространяются правила" },
  { id: "s4", label: "Исключительные случаи возврата" },
  { id: "s5", label: "Порядок подачи заявления на возврат" },
  { id: "s6", label: "Сроки рассмотрения заявления" },
  { id: "s7", label: "Способ и сроки возврата средств" },
  { id: "s8", label: "Продление и завершение подписки" },
  { id: "s9", label: "Отказ в возврате" },
  { id: "s10", label: "Изменение правил" },
  { id: "s11", label: "Контактная информация" },
]

const InfoCard = ({ items }: { items: { label: string; value: React.ReactNode }[] }) => (
  <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 my-4">
    <dl>
      {items.map((item) => (
        <div key={item.label} className="mt-3 first:mt-0">
          <dt className="text-xs font-semibold text-gray-900">{item.label}</dt>
          <dd className="text-sm text-gray-600 mt-0.5">{item.value}</dd>
        </div>
      ))}
    </dl>
  </div>
)

const Callout = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-orange-50 border border-orange-100 border-l-4 border-l-orange-500 rounded-lg p-4 my-4">
    <p className="text-sm text-gray-700 m-0">{children}</p>
  </div>
)

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-xl font-bold text-gray-900 mb-3 pb-1 border-b-2 border-blue-600 inline-block">{children}</h2>
)

export const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <a href="/" className="flex items-center">
            <img src="/horizontal-1.png" alt="Vkonekte" className="h-32 w-auto" />
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <section>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">Правила возврата денежных средств</h1>
          <p className="text-base text-gray-500 mb-1">платформы и мобильного приложения Vkonekte (vkonekte.com)</p>
          <p className="text-sm text-gray-400 italic mb-10">Редакция от 30.07.2026. Действует с даты публикации.</p>

          <nav className="bg-gray-50 border border-gray-100 rounded-xl p-6 mb-12">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Содержание</p>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              {TOC_ITEMS.map((item, i) => (
                <li key={item.id} className="text-sm">
                  <a href={`#${item.id}`} className="text-blue-600 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="text-gray-700 leading-relaxed">
            <section id="s1" className="mb-10">
              <H2>1. Общие положения</H2>
              <p className="mb-3">1.1. Настоящие Правила возврата денежных средств (далее — «Правила») определяют условия и порядок возврата денежных средств, уплаченных Пользователем за платные услуги платформы и мобильного приложения Vkonekte (далее — «Платформа»), предоставляемые Обществом с ограниченной ответственностью «ГС.ГЛОБАЛ» (далее — «Исполнитель»).</p>
              <p className="mb-3">1.2. Настоящие Правила являются неотъемлемой частью Публичной оферты Vkonekte и применяются наряду с ней. В части, не урегулированной настоящими Правилами, действуют условия Публичной оферты и законодательство Кыргызской Республики.</p>
            </section>

            <section id="s2" className="mb-10">
              <H2>2. Общий принцип: платежи не возвращаются</H2>
              <Callout>
                2.1. <strong className="text-gray-900">По общему правилу денежные средства, уплаченные за платные услуги Платформы, возврату не подлежат.</strong> Это связано с тем, что оплаченный доступ (к курсу, лимитам свайпов или сегменту «Китай») предоставляется Пользователю немедленно после подтверждения платежа, то есть услуга считается оказанной с момента открытия доступа.
              </Callout>
              <p className="mb-3">2.2. Из общего правила, изложенного в п. 2.1, действуют исключения, прямо перечисленные в разделе 4 настоящих Правил, а также случаи, прямо предусмотренные законодательством Кыргызской Республики.</p>
            </section>

            <section id="s3" className="mb-10">
              <H2>3. Услуги, на которые распространяются правила</H2>
              <div className="overflow-x-auto mb-3">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold">Услуга</th>
                      <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold">Что оплачивается</th>
                      <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold">Возврат по общему правилу</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 align-top">GS Academy</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Доступ к курсу ментора</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Не производится</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 align-top">Jobs</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Лимиты на свайпы</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Не производится</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2 align-top">Китай</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Подписка на доступ к базе контактов (за оплаченный период)</td>
                      <td className="border border-gray-200 px-3 py-2 align-top">Не производится</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mb-3">Стоимость курсов GS Academy устанавливается самим ментором-автором курса; Исполнитель не участвует в определении цены курса и не является стороной сделки купли-продажи курса между ментором и покупателем, а лишь обеспечивает техническую возможность оплаты и доступа к материалам.</p>
            </section>

            <section id="s4" className="mb-10">
              <H2>4. Исключительные случаи возврата</H2>
              <p className="mb-3">Возврат денежных средств может быть произведён, если Пользователь докажет наступление одного из следующих случаев:</p>
              <ul className="list-disc list-inside space-y-2 mb-3">
                <li><strong className="text-gray-900">Техническая ошибка платформы</strong> — денежные средства были списаны, но оплаченный доступ (к курсу, лимитам свайпов или подписке на сегмент «Китай») не был предоставлен Пользователю по вине Платформы;</li>
                <li><strong className="text-gray-900">Двойное или ошибочное списание</strong> — с Пользователя было списано более одного платежа за одну и ту же услугу в рамках одной операции, либо списание произошло без соответствующего действия Пользователя;</li>
                <li><strong className="text-gray-900">Иные случаи</strong>, при которых обязательность возврата прямо предусмотрена законодательством Кыргызской Республики.</li>
              </ul>
              <p className="mb-3">В случае подтверждения одного из указанных случаев денежные средства возвращаются Пользователю в течение <strong className="text-gray-900">7 (семи) календарных дней</strong> с даты одобрения заявления (без учёта дополнительного времени, необходимого платёжной системе и банку Пользователя на зачисление средств — см. раздел 7).</p>
              <Callout>Лимиты на свайпы в сегменте «Jobs» и подписка на сегмент «Китай» являются тарифами Платформы и не подлежат возврату ни при каких обстоятельствах, кроме случая технической ошибки, прямо указанного выше (списание средств без фактического предоставления доступа).</Callout>
              <p className="mb-3">Возврат не производится, если доступ к оплаченной услуге был фактически предоставлен и функционировал корректно (в том числе если Пользователь не воспользовался курсом, лимитами свайпов или подпиской по собственным причинам, включая изменение решения, отсутствие времени или неудовлетворённость содержанием, добросовестно предоставленным автором/ментором).</p>
            </section>

            <section id="s5" className="mb-10">
              <H2>5. Порядок подачи заявления на возврат</H2>
              <p className="mb-3">
                5.1. Для рассмотрения вопроса о возврате денежных средств в случаях, указанных в разделе 4, Пользователь направляет заявление на электронную почту{" "}
                <a href="mailto:vkonekte.official@gmail.com" className="text-blue-600 hover:underline">vkonekte.official@gmail.com</a>.
              </p>
              <p className="mb-3">5.2. В заявлении необходимо указать:</p>
              <ul className="list-disc list-inside space-y-2 mb-3">
                <li>e-mail и/или номер телефона учётной записи Пользователя;</li>
                <li>дату и приблизительное время платежа;</li>
                <li>наименование оплаченной услуги (курс, лимиты свайпов, подписка на сегмент «Китай»);</li>
                <li>описание проблемы и, по возможности, подтверждающие материалы (скриншоты, чек платёжной системы Freedom).</li>
              </ul>
              <p className="mb-3">5.3. Заявление, поданное без указания сведений, необходимых для идентификации платежа, может быть возвращено Пользователю для уточнения — в этом случае срок рассмотрения, указанный в разделе 6, исчисляется заново с момента получения уточнённого заявления.</p>
            </section>

            <section id="s6" className="mb-10">
              <H2>6. Сроки рассмотрения заявления</H2>
              <p className="mb-3">6.1. Заявление на возврат по основаниям, указанным в разделе 4, рассматривается Исполнителем в течение <strong className="text-gray-900">10 (десяти) рабочих дней</strong> с даты его получения.</p>
              <p className="mb-3">6.2. По результатам рассмотрения Исполнитель направляет Пользователю ответ на e-mail, указанный в заявлении (или e-mail учётной записи), с решением об одобрении либо отказе в возврате, с указанием причины отказа.</p>
            </section>

            <section id="s7" className="mb-10">
              <H2>7. Способ и сроки возврата средств</H2>
              <p className="mb-3">7.1. В случае одобрения заявления, Исполнитель инициирует возврат денежных средств в течение 7 (семи) календарных дней с даты одобрения (см. раздел 4), тем же способом оплаты, которым была произведена первоначальная транзакция, через платёжную систему Freedom.</p>
              <p className="mb-3">7.2. Фактический срок зачисления возвращённых денежных средств на счёт/карту Пользователя дополнительно зависит от правил и сроков обработки платёжной системы Freedom и обслуживающего банка Пользователя и не входит в 7-дневный срок, указанный в п. 7.1.</p>
            </section>

            <section id="s8" className="mb-10">
              <H2>8. Продление и завершение подписки</H2>
              <p className="mb-3">8.1. Подписка на сегмент «Китай» не продлевается автоматически: Платформа не сохраняет данные банковской карты Пользователя и не производит повторное списание средств без его непосредственного действия.</p>
              <p className="mb-3">8.2. По окончании оплаченного периода доступ к сегменту «Китай» приостанавливается. Для возобновления доступа Пользователь самостоятельно совершает новую оплату через настройки личного кабинета — денежные средства за уже истёкший период при этом не возвращаются.</p>
            </section>

            <section id="s9" className="mb-10">
              <H2>9. Отказ в возврате</H2>
              <p className="mb-3">Исполнитель вправе отказать в возврате денежных средств, если:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>заявление подано по основаниям, не предусмотренным разделом 4 настоящих Правил;</li>
                <li>оплаченная услуга была фактически предоставлена и функционировала корректно;</li>
                <li>Пользователем не предоставлены сведения и/или подтверждающие материалы, достаточные для проверки заявления;</li>
                <li>с момента совершения платежа прошло более 30 (тридцати) календарных дней.</li>
              </ul>
            </section>

            <section id="s10" className="mb-10">
              <H2>10. Изменение правил</H2>
              <p className="mb-3">Исполнитель вправе в одностороннем порядке вносить изменения в настоящие Правила. Актуальная редакция всегда доступна в Приложении и/или на сайте vkonekte.com. К заявлениям на возврат применяются Правила, действовавшие на момент совершения платежа.</p>
            </section>

            <section id="s11" className="mb-10">
              <H2>11. Контактная информация</H2>
              <InfoCard
                items={[
                  { label: "Исполнитель", value: "Общество с ограниченной ответственностью «ГС.ГЛОБАЛ» (ОсОО «ГС.ГЛОБАЛ»)" },
                  { label: "Регистрационный номер", value: "314336-3301-ООО" },
                  { label: "ИНН", value: "01903202510355" },
                  { label: "Код ОКПО", value: "33758522" },
                  { label: "Адрес", value: "Кыргызская Республика, г. Бишкек, Первомайский район, ул. Максима Горького, 27/1" },
                  { label: "Телефон", value: <a href="tel:+996507615335" className="text-blue-600 hover:underline">+996 507 615 335</a> },
                  { label: "E-mail по вопросам возврата средств", value: <a href="mailto:vkonekte.official@gmail.com" className="text-blue-600 hover:underline">vkonekte.official@gmail.com</a> },
                ]}
              />
            </section>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-100 mt-16 py-8 text-center text-sm text-gray-400">
        © {new Date().getFullYear()} Vkonekte. All rights reserved.
      </footer>
    </div>
  )
}